import React, {
	memo,
	type MouseEvent,
	type KeyboardEvent,
	useCallback,
	useRef,
	type Ref,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import { statusCategoryForId } from '@jira/platform__status-categories/src/status-categories.tsx';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { shouldDisplayStrikethrough as calculateShouldDisplayStrikethrough } from '@atlassian/jira-issue-key-with-resolution/src/common/utils.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsEmbedMode } from '@atlassian/jira-issue-view-embed-mode/src/index.tsx';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import { useGlobalObjectPreviewPanels } from '@atlassian/jira-preview-panels/src/PreviewPanelsProvider.tsx';
import { useShouldShowIframeIssuePreviewPanel } from '@atlassian/jira-preview-panels-rollout';
import {
	useIsDensityFull,
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
	useIsMergedColumnVisible,
} from '../../../../controllers/features/selectors.tsx';
import ErrorCell from '../../error-cell/index.tsx';
import { TableCellLink, TableCellDiscreteLink } from '../../table-cell-link/index.tsx';
import type { IssueKeyCell_fields$key } from './__generated__/IssueKeyCell_fields.graphql';
import type { IssueKeyCell$key } from './__generated__/IssueKeyCell.graphql';
import { ActionContainer } from './action-container/index.tsx';
import { CopyIssueLink } from './copy-issue-link/index.tsx';
import messages from './messages.tsx';

export type Props = {
	fieldRef: IssueKeyCell$key;
	issueRef?: IssueKeyCell_fields$key | null;
	isRowHoveredOrSelected?: boolean;
	isTruncated?: boolean;
	/**
	 * Dispatched when a table cell with a link to the issue is selected. This method should return `true` to signal
	 * that the event has been handled, which will prevent default event handling.
	 */
	onIssueLinkSelected?: (issueKey: string) => boolean;
};

type OpenPreviewPanel = (props: {
	url: string;
	ari: string;
	name: string;
	iconUrl?: string | undefined;
}) => void;

type IssueKeyStrikeThroughProps = {
	'aria-label'?: string;
	shouldDisplayStrikethrough?: boolean;
};

export const IssueKeyCell = memo<Props>(
	({ fieldRef, isRowHoveredOrSelected, isTruncated, issueRef, onIssueLinkSelected }: Props) => {
		const intl = useIntl();
		const issueKeyData = useFragment<IssueKeyCell$key>(
			graphql`
				fragment IssueKeyCell on JiraSingleLineTextField {
					id
					text
				}
			`,
			fieldRef,
		);

		const { text: issueKey } = issueKeyData;

		const fieldsByIdFragment = useFragment<IssueKeyCell_fields$key>(
			graphql`
				fragment IssueKeyCell_fields on JiraIssue
				@argumentDefinitions(
					isIframePreviewPanelInSubtasksEnabled: {
						type: "Boolean!"
						provider: "@atlassian/jira-relay-provider/src/is-iframe-preview-panel-in-subtasks-enabled.relayprovider"
					}
					isRemoteLinkedIssuesEnabled: { type: "Boolean!" }
				) {
					id @include(if: $isIframePreviewPanelInSubtasksEnabled)
					isResolved
					statusField {
						status {
							statusCategory {
								statusCategoryId
							}
						}
					}
					isRemoteIssue @include(if: $isRemoteLinkedIssuesEnabled)
					webUrl @include(if: $isRemoteLinkedIssuesEnabled)
				}
			`,
			issueRef || null,
		);

		const isRemoteIssue = fieldsByIdFragment?.isRemoteIssue;
		const webUrl = fieldsByIdFragment?.webUrl;
		let toolTipContent = issueKey || '';

		if (isRemoteIssue && webUrl && fg('jira-linked-work-items-targeting-gate')) {
			let remoteServerHostname = '';
			try {
				remoteServerHostname = new URL(webUrl).hostname;
			} catch (e) {
				/* empty */
			}

			toolTipContent = remoteServerHostname || toolTipContent;
		}
		const issueAriId = fieldsByIdFragment?.id;
		const issueUrl =
			isRemoteIssue && webUrl && fg('jira-linked-work-items-targeting-gate')
				? webUrl
				: `/browse/${issueKey}`;
		const isDensityFull = useIsDensityFull();
		const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
		const isInlineEditingExtendedFieldSupportEnabled =
			useIsInlineEditingExtendedFieldSupportEnabled();
		let showCopyIssueLink = false;
		if (fg('jsc-2733-copy_link_from_issue_key')) {
			showCopyIssueLink = !!isRowHoveredOrSelected;
		}

		const tableCellLinkRef = useRef<HTMLAnchorElement>(null);

		let isMergedColumnVisible = false;
		if (fg('jira_list_merged_column_gate')) {
			// eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			isMergedColumnVisible = useIsMergedColumnVisible();
		}

		const hoverPopoverRefFactory = isSingleLineRowHeightEnabled
			? // eslint-disable-next-line react-hooks/react-compiler
				// eslint-disable-next-line react-hooks/rules-of-hooks
				useHoverPopoverContentRef(issueKeyData.id)
			: null;

		let hoverPopoverRef: HoverPopoverContentRef = null;
		if (hoverPopoverRefFactory) {
			hoverPopoverRef = hoverPopoverRefFactory({
				content: issueKey,
			});
		}

		const isEmbedMode = useIsEmbedMode();

		let shouldShowIframePreviewPanel: () => boolean = useCallback(() => {
			return false;
		}, []);
		let openIframePreviewPanel: OpenPreviewPanel = useCallback(() => {
			/* empty */
		}, []);

		if (fg('issue_preview_panel_issue_view_subtask')) {
			// eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const shouldShowIssueInIframePreviewPanel = useShouldShowIframeIssuePreviewPanel();
			shouldShowIframePreviewPanel = shouldShowIssueInIframePreviewPanel;
		}

		if (shouldShowIframePreviewPanel()) {
			// eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const { openPreviewPanel } = useGlobalObjectPreviewPanels();
			openIframePreviewPanel = openPreviewPanel;
		}

		const onClick = useCallback(
			(e: MouseEvent | KeyboardEvent) => {
				if (isRemoteIssue && webUrl && fg('jira-linked-work-items-targeting-gate')) {
					return;
				}

				if (shouldShowIframePreviewPanel()) {
					e.preventDefault();

					openIframePreviewPanel({
						ari: issueAriId || '',
						url: issueUrl,
						name: 'jira-issue',
					});
					return;
				}

				// Use default browser event handling when a modifier key is pressed during the click event
				const isModifierKeyUsed = e.ctrlKey || e.shiftKey || e.metaKey;

				const handled =
					!isEmbedMode &&
					!isModifierKeyUsed &&
					onIssueLinkSelected !== undefined &&
					issueKey &&
					onIssueLinkSelected(issueKey);
				if (handled) {
					e.preventDefault();
				}
			},
			[
				isRemoteIssue,
				webUrl,
				shouldShowIframePreviewPanel,
				isEmbedMode,
				onIssueLinkSelected,
				issueKey,
				openIframePreviewPanel,
				issueAriId,
				issueUrl,
			],
		);

		const issueKeyWithHoverPopover = useCallback(
			({
				link,
			}: {
				link: (ref?: Ref<HTMLAnchorElement> | HoverPopoverContentRef) => React.JSX.Element;
			}) => {
				return link(hoverPopoverRef);
			},
			[hoverPopoverRef],
		);

		if (!issueKey) {
			return <ErrorCell cellType="issueKey" reason="Missing required fields" />;
		}

		const issueStrikeThroughProps: IssueKeyStrikeThroughProps = {};

		const statusField = fieldsByIdFragment?.statusField?.status;

		const statusCategoryId = statusField?.statusCategory?.statusCategoryId;

		const isResolved = fieldsByIdFragment?.isResolved ?? undefined;
		const isIssueResolved = calculateShouldDisplayStrikethrough(
			isResolved,
			statusCategoryForId(statusCategoryId),
		);
		issueStrikeThroughProps.shouldDisplayStrikethrough = isIssueResolved;
		issueStrikeThroughProps['aria-label'] = intl.formatMessage(messages.issueKeyWithResolution, {
			issueKey,
			isResolved: isIssueResolved,
		});

		const getWrapperStyles = () => {
			if (isDensityFull) {
				return isVisualRefreshEnabled() ? wrapperStylesFull : wrapperStylesFullOld;
			}
			return isVisualRefreshEnabled() ? wrapperStylesCompact : wrapperStylesCompactOld;
		};

		if (isInlineEditingExtendedFieldSupportEnabled) {
			const issueKeyLink = (ref: Ref<HTMLAnchorElement> = null) => (
				<TableCellLink
					{...issueStrikeThroughProps}
					href={issueUrl}
					isTruncated={!isDensityFull || isSingleLineRowHeightEnabled}
					onClick={onClick}
					ref={mergeRefs(tableCellLinkRef, ref)}
					testId="native-issue-table.common.ui.issue-cells.issue-key.issue-key-cell"
				>
					{issueKey}
				</TableCellLink>
			);

			const issueKeyLinkWithWrapper = (
				<Box
					paddingBlock="space.050"
					paddingInline={isMergedColumnVisible ? 'space.050' : 'space.100'}
					data-vc="native-issue-table-ui-issue-key-cell"
					xcss={[isMergedColumnVisible && isTruncated && overflowHiddenStyles]}
				>
					{isSingleLineRowHeightEnabled ? (
						issueKeyWithHoverPopover({ link: issueKeyLink })
					) : (
						<Tooltip content={toolTipContent}>{issueKeyLink()}</Tooltip>
					)}
				</Box>
			);

			return fg('jsc-2733-copy_link_from_issue_key') ? (
				<ActionContainer>
					{issueKeyLinkWithWrapper}
					{showCopyIssueLink && <CopyIssueLink issueKey={issueKey} />}
				</ActionContainer>
			) : (
				issueKeyLinkWithWrapper
			);
		}

		const issueKeyLink = (ref: Ref<HTMLAnchorElement> = null) => (
			<TableCellDiscreteLink
				href={issueUrl}
				data-testid="native-issue-table.common.ui.issue-cells.issue-key.issue-key-cell"
				{...issueStrikeThroughProps}
				isSubtle
				ref={mergeRefs(tableCellLinkRef, ref)}
				onClick={onClick}
			>
				{issueKey}
			</TableCellDiscreteLink>
		);

		const issueKeyLinkWithWrapper = (
			<Box
				paddingBlock="space.050"
				paddingInline={isMergedColumnVisible ? 'space.050' : 'space.100'}
				xcss={[
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
					getWrapperStyles(),
					isSingleLineRowHeightEnabled && singleLineRowHeightStyles,
					isMergedColumnVisible && !isTruncated && overflowVisibleStyles,
				]}
				data-vc="native-issue-table-ui-issue-key-cell"
			>
				{isSingleLineRowHeightEnabled ? (
					issueKeyWithHoverPopover({ link: issueKeyLink })
				) : (
					<Tooltip content={toolTipContent}>{issueKeyLink()}</Tooltip>
				)}
			</Box>
		);

		return fg('jsc-2733-copy_link_from_issue_key') ? (
			<ActionContainer>
				{issueKeyLinkWithWrapper}
				{showCopyIssueLink && <CopyIssueLink issueKey={issueKey} />}
			</ActionContainer>
		) : (
			issueKeyLinkWithWrapper
		);
	},
);

const singleLineRowHeightStyles = xcss({
	textOverflow: 'ellipsis',
});

const wrapperStylesCompactOld = xcss({
	font: token('font.heading.xxsmall'),
	maxWidth: '100%',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
});

const wrapperStylesCompact = xcss({
	font: token('font.body'),
	maxWidth: '100%',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
});

const wrapperStylesFullOld = xcss({
	font: token('font.heading.xxsmall'),
	maxWidth: '100%',
});

const wrapperStylesFull = xcss({
	font: token('font.body'),
	maxWidth: '100%',
});

const overflowVisibleStyles = xcss({
	overflow: 'visible',
});

const overflowHiddenStyles = xcss({
	overflow: 'hidden',
});
